import React, { useMemo, useRef, useState } from 'react';
import { Select } from 'chakra-react-select';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Badge, Flex, Text } from '@chakra-ui/react';
import { IUser, useUsers } from '../../../../hooks/useUsers';
import { IData } from '../../../../types/shared';

// Cliente vinculado ao estatuto (#25) — o "cliente" é o usuário do portal (grupo_id=7).
// Vínculo DIRETO a um cliente específico, além do por-empresa. Opcional e editável depois
// (mesmo molde do EmpresaSelect: react-query + debounce + chakra-react-select, só SELEÇÃO —
// cliente não se cadastra por aqui). Mostra ID / imagem / nome / e-mail / status no dropdown.

// Grupo do cliente do portal (Área VIP). Espelha GRUPO_CLIENTE_ID=7 no backend.
const GRUPO_CLIENTE_ID = 7;

export interface IClienteLite {
  id: number;
  nome: string;
  email?: string;
  ativo?: boolean;
  imagem_url?: string;
}

interface ClienteSelectProps {
  value: IClienteLite | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (cliente: IClienteLite | null) => void;
}

interface ClienteOption {
  value: number;
  label: React.ReactNode;
  cliente: IClienteLite;
}

const toLite = (u: IUser): IClienteLite => ({
  id: u.id as number,
  nome: u.nome,
  email: u.email,
  ativo: u.ativo,
  imagem_url: u.imagem_url,
});

// Linha rica do dropdown/valor: avatar + (nome · #id) + e-mail + badge de status. Vai no
// `label` (ReactNode) da opção — como a busca é server-side (filterOption={null}) e o texto
// digitado é controlado por inputValue, o label pode ser um nó visual sem quebrar o filtro.
// `compact` (valor selecionado) esconde o e-mail p/ caber na caixa fechada.
const clienteLabel = (c: IClienteLite, compact = false): React.ReactNode => (
  <Flex align="center" gap={2} minW={0}>
    <Avatar size="xs" name={c.nome} src={c.imagem_url} />
    <Flex direction="column" minW={0} lineHeight="1.15">
      <Text fontSize="sm" fontWeight="600" noOfLines={1}>
        {c.nome}{' '}
        <Text as="span" fontSize="11px" color="gray.400" fontWeight="500">
          #{c.id}
        </Text>
      </Text>
      {!compact && c.email && (
        <Text fontSize="11px" color="gray.500" noOfLines={1}>
          {c.email}
        </Text>
      )}
    </Flex>
    <Badge
      ml="auto"
      fontSize="9px"
      colorScheme={c.ativo === false ? 'gray' : 'green'}
      variant="subtle"
    >
      {c.ativo === false ? 'Inativo' : 'Ativo'}
    </Badge>
  </Flex>
);

export const ClienteSelect: React.FC<ClienteSelectProps> = ({ value, onChange }) => {
  const { getUsers } = useUsers();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const onInputChange = (val: string) => {
    setSearch(val);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(val), 400);
  };

  const { data, isFetching } = useQuery<IData<IUser[]>>({
    // grupo_id=7 recorta só os clientes do portal; `search` é a busca por nome/e-mail no
    // backend (mesmo endpoint /usuarios/). Só busca com o menu aberto (enabled).
    queryKey: [
      'clientes-select',
      { grupo_id: GRUPO_CLIENTE_ID, search: debounced || undefined, page: 1, size: 20 },
    ],
    queryFn: getUsers,
    enabled: menuOpen,
  });

  const options: ClienteOption[] = useMemo(
    () =>
      (data?.data?.items ?? [])
        .filter(u => u.id != null)
        .map(u => {
          const lite = toLite(u);
          return { value: lite.id, label: clienteLabel(lite), cliente: lite };
        }),
    [data],
  );

  return (
    <Select<ClienteOption>
      placeholder="Buscar cliente por nome ou e-mail…"
      options={options}
      value={value ? { value: value.id, label: clienteLabel(value, true), cliente: value } : null}
      inputValue={search}
      onInputChange={onInputChange}
      onMenuOpen={() => setMenuOpen(true)}
      onMenuClose={() => setMenuOpen(false)}
      onChange={opt => onChange(opt ? opt.cliente : null)}
      isLoading={isFetching}
      isClearable
      filterOption={null}
      noOptionsMessage={() => (debounced ? 'Nenhum cliente encontrado' : 'Digite para buscar')}
      loadingMessage={() => 'Buscando…'}
      size="sm"
    />
  );
};
